import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Phone, PlusCircle, Zap, Globe2, ShieldCheck,
  Truck as TruckIcon, Sprout, Factory, Warehouse, ChevronDown, Menu, X, MapPin,
  BadgeIndianRupee, FileCheck2, Landmark,
} from "lucide-react";
import { ROLES } from "@/lib/roles";
import { HOT_PRODUCTS, PRODUCT_CATEGORIES } from "@/lib/products";
import { SUBSIDY_CATEGORIES } from "@/lib/subsidies";
import { LANGS, WHATSAPP_ADMIN } from "@/lib/i18n";
import { useI18n } from "@/App";
import RoleModal from "@/components/RoleModal";
import QuoteModal from "@/components/QuoteModal";
import SubsidyModal from "@/components/SubsidyModal";
import SparePartsSection from "@/components/SparePartsSection";
import { Instagram } from "lucide-react";

const COLLAGE = {
  tl: "https://customer-assets.emergentagent.com/job_eco-trade-zone/artifacts/d5doujed_labour%20pic.webp",     // labour scene
  tr: "https://customer-assets.emergentagent.com/job_eco-trade-zone/artifacts/ivh54cnp_manufactring.webp",     // manufacturing plant
  bl: "https://customer-assets.emergentagent.com/job_eco-trade-zone/artifacts/isw5ywe6_buyer%20seller.jpg",    // pellets in hands
  br: "https://customer-assets.emergentagent.com/job_eco-trade-zone/artifacts/0g1amca3_trader%201111.png",     // BIOMASS ZONE brand panel
};

const CENTER_IMG =
  "https://images.unsplash.com/photo-1595438280062-3c216f8f0d02?auto=format&fit=crop&w=600&q=80"; // pellets/briquettes

export default function Landing() {
  const { t, lang, setLang } = useI18n();
  const [activeRole, setActiveRole] = useState(null);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [productCat, setProductCat] = useState("ALL");
  const [quoteProduct, setQuoteProduct] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [activeSubsidy, setActiveSubsidy] = useState(null);
  const [subsidyOpen, setSubsidyOpen] = useState(false);

  const openRole = (role) => { setActiveRole(role); setOpen(true); };
  const openQuote = (p) => { setQuoteProduct(p); setQuoteOpen(true); };
  const openSubsidy = (s) => { setActiveSubsidy(s); setSubsidyOpen(true); };

  const enquireProduct = (p) => {
    const msg = encodeURIComponent(
      `🌱 *Biomass Zone — Product Enquiry*\n\n• *Product:* ${p.name}\n• *Price:* ${p.price}\n• *Availability:* ${p.availability}\n• *Offer:* ${p.offer}\n\nHi, I'm interested in this product. Please share details.`
    );
    window.open(`https://wa.me/${WHATSAPP_ADMIN}?text=${msg}`, "_blank");
  };

  const filteredProducts = productCat === "ALL"
    ? HOT_PRODUCTS
    : HOT_PRODUCTS.filter((p) => p.tag === productCat);

  const heroWhatsapp = (mode) => {
    const msg = encodeURIComponent(
      `🌱 *Biomass Zone* — ${mode === "buy" ? "New Requirement" : "New Sale Offer"}\n\nHi, I want to ${mode === "buy" ? "post a requirement (buy)" : "sell biomass (pellets/briquettes/residue)"}. Please help me connect.`
    );
    window.open(`https://wa.me/${WHATSAPP_ADMIN}?text=${msg}`, "_blank");
  };

  return (
    <div className="relative min-h-screen">
      {/* Right-side stacked contact rail */}
      <div className="fixed right-0 top-1/3 z-40 hidden md:flex flex-col gap-2 pr-1" data-testid="contact-rail">
        <a href="#home" className="w-11 h-11 rounded-l-lg bg-slate-900 text-white flex items-center justify-center shadow-lg hover:bg-slate-800 transition" title="Top">
          <ArrowRight className="w-4 h-4 -rotate-90" />
        </a>
        <a href={`https://wa.me/${WHATSAPP_ADMIN}`} target="_blank" rel="noreferrer"
          className="w-11 py-4 rounded-l-lg bg-orange-500 text-white flex items-center justify-center shadow-lg hover:bg-orange-600 transition"
          style={{ writingMode: "vertical-rl" }} data-testid="rail-contact-us">
          <span className="text-xs tracking-widest uppercase font-semibold">{t("contact_us")}</span>
        </a>
        <a href="https://www.instagram.com/biomasszone?igsh=MXZ6Z200NnRvbWl0bg==" target="_blank" rel="noreferrer"
          data-testid="rail-instagram"
          className="w-11 h-11 rounded-l-lg text-white flex items-center justify-center shadow-lg transition hover:opacity-90"
          style={{ background: "linear-gradient(135deg,#feda75 0%,#fa7e1e 25%,#d62976 50%,#962fbf 75%,#4f5bd5 100%)" }}>
          <Instagram className="w-5 h-5" />
        </a>
        <a href={`https://wa.me/${WHATSAPP_ADMIN}`} target="_blank" rel="noreferrer"
          data-testid="rail-whatsapp"
          className="w-11 h-11 rounded-l-lg bg-green-500 text-white flex items-center justify-center shadow-lg hover:bg-green-600 transition">
          <Phone className="w-5 h-5" />
        </a>
      </div>

      {/* Floating bottom-right WhatsApp bubble */}
      <div className="fixed bottom-6 right-6 z-40 flex items-center gap-2" data-testid="floating-whatsapp">
        <span className="hidden sm:inline bg-white text-slate-700 text-sm px-3 py-1.5 rounded-full shadow border border-slate-200">
          {lang === "HI" ? "संपर्क करें" : "Contact us"}
        </span>
        <a href={`https://wa.me/${WHATSAPP_ADMIN}`} target="_blank" rel="noreferrer"
          className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center shadow-xl transition"
          aria-label="WhatsApp"
          style={{ boxShadow: "0 12px 30px -8px rgba(34,197,94,0.55)" }}>
          <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden="true">
            <path d="M20.52 3.48A11.94 11.94 0 0012.06 0C5.48 0 .18 5.3.18 11.88c0 2.09.55 4.13 1.6 5.93L0 24l6.35-1.67a11.86 11.86 0 005.71 1.45h.01c6.58 0 11.88-5.3 11.88-11.88 0-3.17-1.23-6.15-3.43-8.42zM12.06 21.7h-.01a9.83 9.83 0 01-5.02-1.38l-.36-.21-3.77.99 1.01-3.67-.23-.38a9.83 9.83 0 01-1.5-5.19c0-5.44 4.43-9.87 9.88-9.87 2.64 0 5.12 1.03 6.99 2.9a9.81 9.81 0 012.89 6.98c0 5.44-4.43 9.83-9.88 9.83zm5.71-7.37c-.31-.16-1.85-.91-2.13-1.02-.29-.11-.5-.16-.7.16-.21.31-.8 1.02-.98 1.23-.18.21-.36.24-.67.08-.31-.16-1.31-.48-2.5-1.53-.92-.82-1.55-1.83-1.73-2.14-.18-.31-.02-.48.14-.63.14-.14.31-.36.47-.55.16-.18.21-.31.31-.52.1-.21.05-.39-.03-.55-.08-.16-.7-1.7-.96-2.32-.25-.6-.51-.52-.7-.53l-.6-.01c-.21 0-.55.08-.83.39-.29.31-1.09 1.07-1.09 2.6s1.12 3.03 1.28 3.24c.16.21 2.2 3.36 5.34 4.71.75.32 1.33.51 1.78.66.75.24 1.43.21 1.97.13.6-.09 1.85-.76 2.11-1.5.26-.74.26-1.36.18-1.5-.08-.13-.29-.21-.6-.37z"/>
          </svg>
        </a>
      </div>

      {/* NAV */}
      <header className="relative z-30 pt-5 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bz-pill-nav flex items-center justify-between px-4 sm:px-6 py-3">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-name">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow">
                <Sprout className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-lg text-emerald-800">Biomass</div>
                <div className="text-[10px] tracking-[0.2em] text-emerald-600 -mt-1">ZONE</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1 text-sm text-slate-700">
              <a href="#home" className="px-3 py-2 text-emerald-700 font-semibold">{t("nav_home")}</a>
              <a href="#products" className="px-3 py-2 hover:text-emerald-700 transition">{lang === "HI" ? "प्रोडक्ट्स खरीदें" : "Buy Products"}</a>
              <a href="#spare-parts" className="px-3 py-2 hover:text-emerald-700 transition">{lang === "HI" ? "स्पेयर पार्ट्स" : "Spare Parts"}</a>
              <a href="#subsidy" className="px-3 py-2 hover:text-emerald-700 transition inline-flex items-center gap-1">
                <BadgeIndianRupee className="w-3.5 h-3.5 text-amber-600" />
                {lang === "HI" ? "सरकारी सब्सिडी" : "Govt. Subsidy"}
              </a>
              <a href="#sectors" className="px-3 py-2 hover:text-emerald-700 transition">{t("nav_we_serve")}</a>
              <a href="#why" className="px-3 py-2 hover:text-emerald-700 transition">{t("nav_solutions")}</a>
              <a href="#sectors" className="px-3 py-2 hover:text-emerald-700 transition">{t("nav_network")}</a>
              <a href="#sectors" className="px-3 py-2 hover:text-emerald-700 transition">{t("nav_marketplace")}</a>

              {/* Lang toggle */}
              <div className="relative ml-2">
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  data-testid="lang-toggle"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-emerald-200 hover:bg-emerald-50 text-emerald-700"
                >
                  <Globe2 className="w-4 h-4" /> {lang}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {langOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white border border-slate-200 rounded-xl shadow-lg py-1" data-testid="lang-menu">
                    {LANGS.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`lang-${l.code}`}
                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 ${lang === l.code ? "text-emerald-700 font-semibold" : "text-slate-700"}`}
                      >{l.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            <div className="flex items-center gap-2">
              <Link to="/admin" data-testid="nav-admin-link"
                className="hidden sm:inline-flex text-xs uppercase tracking-widest text-slate-500 hover:text-emerald-700 px-2">
                {t("nav_admin")}
              </Link>
              <button
                data-testid="nav-login-btn"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirect = window.location.origin + "/";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
                }}
                className="hidden sm:inline-flex text-xs uppercase tracking-widest text-emerald-700 hover:text-emerald-900 px-3 py-1.5 rounded-full border border-emerald-300 hover:bg-emerald-50 items-center gap-2"
              >
                <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                {t("login")}
              </button>
              <a href={`tel:+${WHATSAPP_ADMIN}`} data-testid="nav-call-btn"
                 className="bz-btn-ghost rounded-full px-4 py-2 text-sm hidden sm:inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> {t("call_support")}
              </a>
              <button className="lg:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="menu-toggle">
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {menuOpen && (
            <div className="lg:hidden mt-2 bg-white rounded-2xl border border-slate-200 p-4 shadow-lg space-y-2 text-sm">
              <a href="#sectors" onClick={() => setMenuOpen(false)} className="block py-2">{t("nav_we_serve")}</a>
              <a href="#why" onClick={() => setMenuOpen(false)} className="block py-2">{t("nav_solutions")}</a>
              <Link to="/admin" onClick={() => setMenuOpen(false)} className="block py-2">{t("nav_admin")}</Link>
              <div className="flex gap-2 pt-2">
                {LANGS.map((l) => (
                  <button key={l.code} onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 rounded-full text-xs ${lang === l.code ? "bg-emerald-600 text-white" : "bg-slate-100"}`}>
                    {l.code}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* HERO */}
      <section id="home" className="bz-hero-scene relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="bz-reveal">
            <span className="bz-kicker">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {t("hero_kicker")}
            </span>
            <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-slate-900">
              {t("hero_title_1")} <span className="text-emerald-700">{t("hero_title_2")}</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("hero_desc")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => heroWhatsapp("buy")} data-testid="hero-post-req"
                className="bz-btn-primary rounded-full px-6 py-3 inline-flex items-center gap-2">
                <PlusCircle className="w-4 h-4" /> {t("hero_cta_primary")}
              </button>
              <button onClick={() => heroWhatsapp("sell")} data-testid="hero-sale-now"
                className="bz-btn-gold rounded-full px-6 py-3 inline-flex items-center gap-2">
                <Zap className="w-4 h-4" /> {t("hero_cta_secondary")}
              </button>
              <a href={`tel:+${WHATSAPP_ADMIN}`} data-testid="hero-call"
                className="bz-btn-ghost rounded-full px-6 py-3 inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> {t("call_support")}
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat k="28+" v={t("stats_states")} />
              <Stat k="500+" v={t("stats_partners")} />
              <Stat k="24×7" v={t("stats_support")} />
            </div>
          </div>

          {/* Circular collage */}
          <div className="relative bz-reveal">
            <div className="bz-collage">
              <div className="bz-collage-ring" />
              <div className="bz-quad bz-quad-tl"><img src={COLLAGE.tl} alt="Labour" /></div>
              <div className="bz-quad bz-quad-tr"><img src={COLLAGE.tr} alt="Manufacturing" /></div>
              <div className="bz-quad bz-quad-bl"><img src={COLLAGE.bl} alt="Pellets" /></div>
              <div className="bz-quad bz-quad-br"><img src={COLLAGE.br} alt="Biomass Zone Brand" /></div>
              <div className="bz-collage-center">
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg">
                    <Sprout className="w-9 h-9 text-white" />
                  </div>
                  <div className="font-display text-lg mt-3 text-emerald-800">Biomass Zone</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-600 mt-1">Green Energy Marketplace</div>
                </div>
              </div>
            </div>
            {/* Floating badges */}
            <FloatingBadge className="left-0 top-4" icon={Sprout} label={lang === "HI" ? "किसान" : "Farmer"} />
            <FloatingBadge className="right-0 top-16" icon={Factory} label={lang === "HI" ? "निर्माता" : "Manufacturer"} />
            <FloatingBadge className="left-2 bottom-10" icon={TruckIcon} label={lang === "HI" ? "ट्रांसपोर्टर" : "Transport"} />
            <FloatingBadge className="right-2 bottom-4" icon={Warehouse} label={lang === "HI" ? "उद्योग" : "Industry"} />
          </div>
        </div>
        <div className="bz-hero-ground h-16 md:h-24" />
      </section>

      <section id="sectors" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="bz-kicker">{t("sectors_kicker")}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
              {t("sectors_title")}
            </h2>
            <p className="mt-4 text-slate-600 text-base">{t("sectors_desc")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {ROLES.map((r, idx) => {
              const Icon = r.icon;
              return (
                <button
                  key={r.key}
                  data-testid={`role-card-${r.key.toLowerCase()}`}
                  onClick={() => openRole(r)}
                  className="bz-card group relative overflow-hidden text-left bz-reveal"
                  style={{ animationDelay: `${idx * 60}ms` }}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={r.image} alt={r.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white shadow-md border border-emerald-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-emerald-700" />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-[11px] tracking-widest uppercase text-emerald-700 font-semibold">
                      {r.tagline}
                    </div>
                    <div className="font-display text-xl mt-1 text-slate-900">{r.title}</div>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.description}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                      {lang === "HI" ? "अभी रजिस्टर करें" : "Register now"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTS - Hot Listed Live */}
      <section id="products" className="py-20 sm:py-24 bg-emerald-50/30 border-t border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
            <div>
              <span className="bz-kicker">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                {lang === "HI" ? "हॉट लिस्टेड · लाइव" : "Hot Listed · Live"}
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
                {lang === "HI" ? "प्रोडक्ट्स खरीदें" : "Buy Products"}
              </h2>
              <p className="mt-3 text-slate-600 max-w-xl">
                {lang === "HI"
                  ? "उपलब्धता और लाइव ऑफर के साथ आज के सक्रिय स्टॉक।"
                  : "Today's active stock with live availability and offers. Prices update daily."}
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="product-category-tabs">
              {PRODUCT_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`product-cat-${c.key.toLowerCase()}`}
                  onClick={() => setProductCat(c.key)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                    productCat === c.key
                      ? "bg-emerald-600 text-white border-emerald-600 shadow"
                      : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500"
                  }`}
                >
                  {lang === "HI" ? c.hi : c.en}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((p) => {
              const Icon = p.icon;
              const tagColor =
                p.tag === "HOT" ? "bg-red-500 text-white"
                : p.tag === "LIVE" ? "bg-emerald-600 text-white"
                : "bg-amber-500 text-white";
              return (
                <div
                  key={p.id}
                  data-testid={`product-${p.id}`}
                  className="bz-card overflow-hidden flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image} alt={p.name}
                      className="w-full h-full object-cover" />
                    <span className={`absolute top-3 left-3 text-[10px] tracking-widest font-bold px-2.5 py-1 rounded-full ${tagColor} shadow`}>
                      {p.tag}
                    </span>
                    <div className="absolute top-3 right-3 w-9 h-9 rounded-lg bg-white/95 border border-emerald-100 flex items-center justify-center">
                      <Icon className="w-4.5 h-4.5 text-emerald-700" />
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="font-display text-lg text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.hi}</div>
                    {p.spec && (
                      <div className="text-xs text-slate-600 mt-2 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 inline-block">
                        {p.spec}
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-emerald-700 font-bold text-lg">{p.price}</span>
                      <span className="text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-100">
                        {p.availability}
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5 inline-flex items-center gap-1.5">
                      <Zap className="w-3 h-3" /> {p.offer}
                    </div>
                    <button
                      onClick={() => openQuote(p)}
                      data-testid={`quote-${p.id}`}
                      className="mt-4 bz-btn-primary rounded-full py-2.5 text-sm inline-flex items-center justify-center gap-2"
                    >
                      {lang === "HI" ? "कोट लें" : "Quote Now"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MACHINE SPARE PARTS */}
      <SparePartsSection onInquire={openQuote} />

      {/* GOVT SUBSIDY */}
      <section id="subsidy" className="py-20 sm:py-24 bg-gradient-to-b from-amber-50/60 to-white border-t border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div className="max-w-2xl">
              <span className="bz-kicker">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                <BadgeIndianRupee className="w-3.5 h-3.5 text-amber-600" />
                {lang === "HI" ? "एक्सक्लूसिव · सरकारी योजना" : "Exclusive · Govt. Scheme"}
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
                {lang === "HI" ? "अपनी सरकारी सब्सिडी क्लेम करें" : "Claim Your Govt. Subsidy"}
              </h2>
              <p className="mt-3 text-slate-600 max-w-xl">
                {lang === "HI"
                  ? "6 प्रमुख सब्सिडी योजनाएँ। ज़रूरी डाक्यूमेंट्स अपलोड करें — हमारी टीम पूरी प्रोसेस में आपकी मदद करेगी।"
                  : "6 major subsidy programs. Upload the required documents — our team assists you end-to-end till disbursal."}
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <div className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 rounded-full px-3 py-1.5">
                <FileCheck2 className="w-3.5 h-3.5 text-emerald-600" />
                {lang === "HI" ? "सुरक्षित अपलोड" : "Secure upload"}
              </div>
              <div className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 rounded-full px-3 py-1.5">
                <Landmark className="w-3.5 h-3.5 text-amber-600" />
                {lang === "HI" ? "एक्सपर्ट सपोर्ट" : "Expert support"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="subsidy-grid">
            {SUBSIDY_CATEGORIES.map((s, idx) => (
              <button
                key={s.key}
                data-testid={`subsidy-card-${s.key.toLowerCase()}`}
                onClick={() => openSubsidy(s)}
                className="group relative overflow-hidden text-left rounded-2xl border border-emerald-100 bg-white hover:border-emerald-400 transition-all shadow-sm hover:shadow-xl bz-reveal"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className={`h-1.5 w-full bg-gradient-to-r ${s.color}`} />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-lg`}>
                      <BadgeIndianRupee className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-[10px] tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-1">
                      {s.code}
                    </span>
                  </div>
                  <div className="text-[11px] tracking-widest uppercase text-slate-500 font-semibold mt-5">
                    {s.tagline}
                  </div>
                  <div className="font-display text-xl mt-1 text-slate-900">
                    {lang === "HI" ? s.hi : s.en}
                  </div>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                    {lang === "HI"
                      ? `${s.docs.length} ज़रूरी डाक्यूमेंट्स · अभी अपलोड करें`
                      : `${s.docs.length} required documents · Upload now`}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                    {lang === "HI" ? "क्लेम शुरू करें" : "Start claim"}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="py-20 sm:py-28 bg-gradient-to-b from-emerald-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="bz-kicker">{t("why_kicker")}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
              {t("why_title")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            <Feature icon={ShieldCheck} title={t("why_1_t")}>{t("why_1_d")}</Feature>
            <Feature icon={Zap} title={t("why_2_t")}>{t("why_2_d")}</Feature>
            <Feature icon={MapPin} title={t("why_3_t")}>{t("why_3_d")}</Feature>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-emerald-900 text-emerald-50 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
                <Sprout className="w-5 h-5 text-emerald-200" />
              </div>
              <div className="font-display text-xl">Biomass Zone</div>
            </div>
            <p className="text-sm text-emerald-200/80 mt-4 max-w-xs">{t("footer_tagline")}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-300">Quick</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a href="#sectors" className="hover:text-white">{t("nav_we_serve")}</a></li>
              <li><a href="#why" className="hover:text-white">{t("nav_solutions")}</a></li>
              <li><Link to="/admin" className="hover:text-white" data-testid="footer-admin-link">{t("nav_admin")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-300">{t("contact_us")}</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a href={`https://wa.me/${WHATSAPP_ADMIN}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-white">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-green-400" fill="currentColor"><path d="M20.52 3.48A11.94 11.94 0 0012.06 0C5.48 0 .18 5.3.18 11.88c0 2.09.55 4.13 1.6 5.93L0 24l6.35-1.67a11.86 11.86 0 005.71 1.45h.01c6.58 0 11.88-5.3 11.88-11.88 0-3.17-1.23-6.15-3.43-8.42z"/></svg>
                WhatsApp us instantly (+91 98124 98875)
              </a></li>
              <li><a href="https://www.instagram.com/biomasszone?igsh=MXZ6Z200NnRvbWl0bg==" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-white">
                <Instagram className="w-5 h-5 text-pink-400" /> @biomasszone
              </a></li>
              <li><a href="mailto:biomasszone@gmail.com" className="hover:text-white">biomasszone@gmail.com</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 mt-10 text-xs text-emerald-300/70 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Biomass Zone. {t("footer_rights")}</span>
          <span>Made in India · Powered by verified partners</span>
        </div>
      </footer>

      <RoleModal role={activeRole} open={open} onOpenChange={setOpen} />
      <QuoteModal product={quoteProduct} open={quoteOpen} onOpenChange={setQuoteOpen} />
      <SubsidyModal subsidy={activeSubsidy} open={subsidyOpen} onOpenChange={setSubsidyOpen} />
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="font-display text-3xl text-emerald-800">{k}</div>
      <div className="text-xs text-slate-500 mt-1 uppercase tracking-widest">{v}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, children }) {
  return (
    <div className="bz-card p-6">
      <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-emerald-700" />
      </div>
      <div className="font-display text-xl text-slate-900">{title}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{children}</p>
    </div>
  );
}

function FloatingBadge({ icon: Icon, label, className }) {
  return (
    <div className={`absolute ${className} bg-white rounded-full pl-2 pr-4 py-2 shadow-lg border border-emerald-100 flex items-center gap-2 text-xs font-semibold text-slate-700 hidden md:flex`}>
      <span className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-emerald-700" />
      </span>
      {label}
    </div>
  );
}
